import { serializeFilterDates } from "../core/filter";
import { normalizeSortInfo } from "./local-query";

const isAdvancedGrouping = group =>
  Array.isArray(group) &&
  group.some(
    item => item && typeof item === "object" && ("groupInterval" in item || "isExpanded" in item)
  );

function sortForRequest(sort) {
  return normalizeSortInfo(sort).map(({ selector, desc }) => ({ selector, desc }));
}

export function toRequestParams(loadOptions = {}) {
  const params = {};

  ["skip", "take", "requireTotalCount", "requireGroupCount", "isCountQuery"].forEach(name => {
    if (loadOptions[name] !== undefined) params[name] = loadOptions[name];
  });

  if (loadOptions.sort) params.sort = sortForRequest(loadOptions.sort);

  if (loadOptions.group)
    params.group = isAdvancedGrouping(loadOptions.group)
      ? loadOptions.group
      : sortForRequest(loadOptions.group);

  if (Array.isArray(loadOptions.filter)) params.filter = serializeFilterDates(loadOptions.filter);

  if (loadOptions.totalSummary) params.totalSummary = loadOptions.totalSummary;
  if (loadOptions.groupSummary) params.groupSummary = loadOptions.groupSummary;

  if (loadOptions.select)
    params.select = Array.isArray(loadOptions.select) ? loadOptions.select : [loadOptions.select];

  return params;
}

export function normalizeLoadResult(result) {
  if (Array.isArray(result)) return { data: result, totalCount: -1, groupCount: -1 };
  if (typeof result === "number") return { data: [], totalCount: result, groupCount: -1 };

  const value = result || {};

  return {
    data: Array.isArray(value.data) ? value.data : [],
    totalCount: "totalCount" in value ? value.totalCount : -1,
    groupCount: "groupCount" in value ? value.groupCount : -1,
    summary: value.summary || null,
  };
}

export class GridLoadError extends Error {
  constructor(status, message) {
    super(message || `Grid load failed (${status})`);
    this.status = status;
  }
}

export function createRemoteStore({
  key = "id",
  url,
  method = "POST",
  params,
  prepareHeaders,
  onError,
}) {
  const send = async (loadOptions, requestOptions = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(prepareHeaders ? await prepareHeaders() : {}),
    };
    const actionParams = toRequestParams(loadOptions);
    const body = params ? { ...structuredClone(params), loadOptions: actionParams } : actionParams;

    let response;

    try {
      response = await fetch(url, {
        method,
        headers,
        body: method === "GET" ? undefined : JSON.stringify(body),
        signal: requestOptions.signal,
        cache: "no-store",
      });
    } catch (error) {
      if (error?.name === "AbortError") throw error;

      if (onError) onError({ status: 0, error });
      throw new GridLoadError(0, error?.message);
    }

    if (!response.ok) {
      if (onError) onError({ status: response.status });
      throw new GridLoadError(response.status);
    }

    const text = await response.text();

    return text ? JSON.parse(text) : null;
  };

  return {
    key,
    __remoteStore: true,
    load: (loadOptions = {}, requestOptions) => send(loadOptions, requestOptions),
    totalCount: (loadOptions = {}, requestOptions) =>
      send({ ...loadOptions, isCountQuery: true, requireTotalCount: true }, requestOptions).then(
        result => normalizeLoadResult(result).totalCount
      ),
    byKey: (keyValue, requestOptions) =>
      send({ filter: [key, "=", keyValue] }, requestOptions).then(
        result => normalizeLoadResult(result).data[0]
      ),
  };
}
